var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Button, MessageBanner, Modal, TextArea } from '@folio/stripes/components';
import { useReviewMutation } from '../../hooks/useReviewMutation';
import { DeleteWithRefsDialog } from '../DeleteWithRefsDialog';
import DetailActions from '../DetailActions';
import { replaceUrlFor } from '../../utils/editUrlFor';
import { isTimeoutError } from '../../utils/errorKind';
/** The actions this component draws; `edit` belongs to the surrounding view. */
var REVIEW_ACTIONS = ['approve', 'forward', 'redirect', 'requestChanges', 'cancel'];
/** Whether the record offers any review action at all. */
export var hasReviewActions = function (record) {
    return (record.reviewStatus === 'PENDING_REVIEW' || record.reviewStatus === 'PENDING_NATIONAL_REVIEW')
        && REVIEW_ACTIONS.some(function (action) { var _a; return (_a = record.actions) === null || _a === void 0 ? void 0 : _a.includes(action); });
};
/**
 * CZNA-1432 — which variant of a help text applies. A vocabulary term has a single review
 * stage (CZNA-1311), so its Schválit closes the review where an authority record's would
 * skip Kontrola NK; the record type is what tells them apart, the status the stage.
 */
var helpStage = function (record) {
    var _a;
    // `?.`: the vocabulary edit pane hands over whatever the detail endpoint answered, and
    // a record without a type must not take the whole pane down with it.
    if ((_a = record.type) === null || _a === void 0 ? void 0 : _a.startsWith('VOCABULARY_'))
        return 'vocabulary';
    return record.reviewStatus === 'PENDING_NATIONAL_REVIEW' ? 'national' : 'local';
};
/**
 * CZNA-1456 — the one place that orders the actions over a record, for the detail header
 * and the edit footer alike.
 *
 * The primary is what the user most often does in the situation: the review decision when
 * a review is open (Schválit, or Postoupit NK for whoever may not approve), otherwise
 * Editovat or Navrhnout změnu. Vrátit k doplnění is never primary; it leads the menu
 * (decided 25. 10. 2026). The edit footer passes `withPrimary: false` — Uložit is its
 * primary — and gets the same menu with the review decision back in it.
 *
 * CZNA-1617 — Exportovat MARCXML reads the record and changes nothing, so it is never the
 * primary: it closes the reversible part of the menu, after every action over the record.
 */
var PRIMARY_ORDER = ['approve', 'forward', 'edit', 'proposeChange'];
var MENU_ORDER = [
    'requestChanges', 'approve', 'forward', 'edit', 'proposeChange', 'exportMarcXml',
    'redirect', 'delete', 'merge',
];
export var layoutRecordActions = function (actions, _a) {
    var withPrimary = _a.withPrimary;
    var primaryKey = withPrimary ? PRIMARY_ORDER.find(function (key) { return actions[key]; }) : undefined;
    var menu = MENU_ORDER
        .filter(function (key) { return key !== primaryKey; })
        .map(function (key) { return actions[key]; })
        .filter(function (action) { return !!action; });
    // No candidate, no primary: Vrátit k doplnění or Zrušit must not become one by being first.
    return primaryKey
        ? { actions: __spreadArray([actions[primaryKey]], menu, true), withPrimary: true }
        : { actions: menu, withPrimary: false };
};
var RequestChangesDialog = function (_a) {
    var record = _a.record, onClose = _a.onClose, onRequested = _a.onRequested, onError = _a.onError;
    var intl = useIntl();
    var _b = useState(''), comment = _b[0], setComment = _b[1];
    var _c = useReviewMutation(record.id, {
        onSuccess: onRequested,
        onError: onError,
    }), mutate = _c.mutate, isLoading = _c.isLoading;
    return (React.createElement(Modal, { open: true, label: intl.formatMessage({ id: 'ui-czna-authorities.review.requestChanges.heading' }), onClose: onClose, footer: (React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: '8px' } },
            React.createElement(Button, { onClick: onClose, disabled: isLoading },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.requestChanges.cancel" })),
            React.createElement(Button, { buttonStyle: "primary", disabled: !comment.trim() || isLoading, onClick: function () { return mutate({
                    action: 'request_changes',
                    reviewComment: comment,
                    expectedOptimisticLock: record.optimisticLock,
                }); } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.requestChanges.confirm" })))) },
        React.createElement(TextArea, { label: intl.formatMessage({ id: 'ui-czna-authorities.review.requestChanges.comment' }), value: comment, required: true, maxLength: 2000, onChange: function (event) { return setComment(event.target.value); } })));
};
/**
 * Review workflow actions shared by a published record's detail and edit views.
 *
 * CZNA-1456 — the buttons moved into the detail's pane header and the edit footer's menu,
 * neither of which this owns, so it returns the actions and keeps only what lives in the
 * body. `record` may be undefined while the detail loads, because a hook cannot wait.
 */
export var useRecordReviewActions = function (record, _a) {
    var _b, _c, _d, _e;
    var _f = _a === void 0 ? {} : _a, onActionComplete = _f.onActionComplete, _g = _f.disabled, disabled = _g === void 0 ? false : _g, returnTo = _f.returnTo;
    var intl = useIntl();
    var history = useHistory();
    var _h = useState(null), intent = _h[0], setIntent = _h[1];
    // The message names the record it was about: a detail pane that stays mounted while the user
    // moves to another record must not show the previous record's confirmation on the next one.
    var _j = useState(null), feedback = _j[0], setFeedback = _j[1];
    var recordId = (_b = record === null || record === void 0 ? void 0 : record.id) !== null && _b !== void 0 ? _b : '';
    var heading = (_e = (_d = (_c = record === null || record === void 0 ? void 0 : record.headings) === null || _c === void 0 ? void 0 : _c.find(function (h) { return h.headingType === 'PREFERRED'; })) === null || _d === void 0 ? void 0 : _d.headingValue) !== null && _e !== void 0 ? _e : '';
    var complete = function (action) {
        setFeedback({
            type: 'success',
            message: intl.formatMessage({ id: "ui-czna-authorities.review.action.".concat(action, ".success") }, { heading: heading }),
            recordId: recordId,
        });
        setIntent(null);
        onActionComplete === null || onActionComplete === void 0 ? void 0 : onActionComplete();
    };
    var fail = function (error) {
        var _a;
        var status = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status;
        // CZNA-1625 — no answer in time (client timeout or 504): the action may have been carried
        // out, so "check first", never "failed, try again".
        var messageId = isTimeoutError(error)
            ? 'ui-czna-authorities.error.timeout'
            : status === 409
                ? 'ui-czna-authorities.review.queue.actionConflict'
                : status === 403
                    ? 'ui-czna-authorities.review.queue.actionForbidden'
                    : 'ui-czna-authorities.review.queue.actionError';
        setFeedback({ type: 'error', message: intl.formatMessage({ id: messageId }), recordId: recordId });
    };
    var approve = useReviewMutation(recordId, { onSuccess: function () { return complete('approve'); }, onError: fail });
    var forward = useReviewMutation(recordId, { onSuccess: function () { return complete('forward'); }, onError: fail });
    // `feedback &&` first: a record without an id would otherwise match a missing feedback
    // (`undefined === undefined`) and render a banner out of `null`.
    var banner = feedback && record && feedback.recordId === record.id
        ? React.createElement(MessageBanner, { type: feedback.type }, feedback.message)
        : null;
    // The action that just succeeded usually leaves the caller with nothing more to do here —
    // forward hands the record to NK — so the actions go, but its confirmation stays.
    if (!record || !hasReviewActions(record))
        return { actions: {}, element: banner };
    // CZNA-1321 — every action follows the backend's `actions`, which carry both who may act and
    // in which review state; an action the backend would refuse is not offered.
    var allows = function (action) { var _a, _b; return (_b = (_a = record.actions) === null || _a === void 0 ? void 0 : _a.includes(action)) !== null && _b !== void 0 ? _b : false; };
    var stage = helpStage(record);
    var busy = approve.isLoading || forward.isLoading;
    // CZNA-1577 — „Zrušit s náhradou“ compares both records in full, which does not fit a
    // dialog: it is a page, and the redirect lands on the replacement's detail from there.
    var openRedirectPage = function () {
        setIntent(null);
        history.push({
            pathname: replaceUrlFor(record, 'redirect'),
            state: returnTo ? { returnTo: returnTo } : undefined,
        });
    };
    var action = function (key, onClick, extra) { return (__assign({ key: key, labelId: "ui-czna-authorities.review.action.".concat(key), helpId: "ui-czna-authorities.review.action.".concat(key, ".summary"), helpValues: { stage: stage }, onClick: onClick, disabled: disabled || busy }, extra)); };
    var actions = {};
    if (allows('approve')) {
        actions.approve = action('approve', function () { return approve.mutate({
            action: 'approve',
            expectedOptimisticLock: record.optimisticLock,
        }); }, {
            testAttributes: { 'data-test-review-action': "approve-".concat(record.id), 'data-test-approve-action': record.id },
        });
    }
    if (allows('forward')) {
        actions.forward = action('forward', function () { return forward.mutate({
            action: 'forward',
            expectedOptimisticLock: record.optimisticLock,
        }); }, {
            testAttributes: { 'data-test-review-action': "forward-".concat(record.id), 'data-test-forward-action': record.id },
        });
    }
    if (allows('requestChanges')) {
        actions.requestChanges = action('requestChanges', function () { return setIntent('requestChanges'); }, {
            testAttributes: { 'data-test-request-changes-action': record.id },
        });
    }
    if (allows('redirect')) {
        actions.redirect = action('redirect', openRedirectPage, {
            irreversible: true,
            testAttributes: { 'data-test-redirect-action': record.id },
        });
    }
    if (allows('cancel')) {
        actions.delete = action('delete', function () { return setIntent('delete'); }, {
            irreversible: true,
            testAttributes: { 'data-test-delete-action': record.id },
        });
    }
    var element = (React.createElement(React.Fragment, null,
        banner,
        intent === 'delete' && allows('cancel') ? (React.createElement(DeleteWithRefsDialog, { open: true, recordId: record.id, recordType: record.type, headingValue: heading, onClose: function () { return setIntent(null); }, onDeleted: function () { return complete('delete'); }, onRedirectInstead: allows('redirect') ? openRedirectPage : undefined })) : null,
        intent === 'requestChanges' && allows('requestChanges') ? (React.createElement(RequestChangesDialog, { record: record, onClose: function () { return setIntent(null); }, onRequested: function () { return complete('requestChanges'); }, onError: fail })) : null));
    return { actions: actions, element: element };
};
/**
 * The edit footer's review actions (RecordEdit, VocabularyEdit): the same menu as the
 * detail's, with no primary of its own — Uložit is the footer's primary (CZNA-1456).
 */
var RecordReviewActions = function (_a) {
    var record = _a.record, onActionComplete = _a.onActionComplete, _b = _a.disabled, disabled = _b === void 0 ? false : _b, returnTo = _a.returnTo;
    var _c = useRecordReviewActions(record, { onActionComplete: onActionComplete, disabled: disabled, returnTo: returnTo }), actions = _c.actions, element = _c.element;
    return (React.createElement(React.Fragment, null,
        element,
        React.createElement(DetailActions, __assign({ id: "record-edit-review-actions" }, layoutRecordActions(actions, { withPrimary: false }), { helpIcon: false }))));
};
export default RecordReviewActions;
